import { createSelector } from 'reselect';
import _head from 'lodash/head';
import _get from 'lodash/get';
import _reject from 'lodash/reject';
import _assign from 'lodash/assign';
import { COSTUMIZATION_BAR_DROPDOWN_SECTIONS, QUANTITIES, INVITES_PRODUCT_TYPES } from '../../constants/Cards';

export const getProductId = state => state.cards.project.productId;
export const getProduct = state => state.cards.cardDetail.products[state.cards.cardDetail.activeProduct] || {};
export const getProducts = state => state.cards.cardDetail.products || [];
export const getActiveCardEvent = state => state.cards.project.activeCardEvent;
export const getInitialProductColor = state => state.cards.cardDetail.initialProductColor;
export const getProjecType = state => state.cards.cardProject.projectType;
export const getCustomizationTypeUUIDs = state => state.cards.cardProject.customizationTypeToUUID;
export const getCustomizations = state => state.cards.cardProject.customizations;
export const getInitialColor = state => state.cards.cardDetail.initialProductColor;
export const getSkus = state => state.cards.cardProject.skus;
export const getProjectId = state => state.cards.cardProject.projectUUID;
export const getSteps = state => state.cards.cardProject.steps;
export const getQuantityOptions = QUANTITIES.map(quantity => ({ label: quantity.toString(), value: quantity }));
export const getAddressingOptionStatus = state => state.cards.cardCustomizationBar.hasRecipientAddressing;
export const getSelectedGuestIds = state => state.cards.cardCustomizationBar.selectedRecipientsIds;
export const getSavedGuestIds = state => state.cards.recipients.selectedGuestIds;
const getPathName = state => state.routing.locationBeforeTransitions.pathname;

export const getUuid = createSelector(
  getProjecType,
  getCustomizationTypeUUIDs,
  (type, uuids) => uuids[type],
);

export const getCombinations = createSelector(
  getProduct,
  product => product.combinations
);

export const getCustomizableUUIDs = createSelector(
  getCustomizationTypeUUIDs,
  getCustomizations,
  getSkus,
  (types, customizations, skus) => Object.keys(types).reduce((result, type) => {
    if (INVITES_PRODUCT_TYPES.includes(type) && customizations[types[type]]) {
      const customization = customizations[types[type]];
      const sku = skus[customization.sku_object_id];
      if (customization && sku) {
        result.push({
          uuid: types[type],
          sku: customization.sku_object_id,
          product_option_values: sku.product_option_values,
          product_object_id: sku.product_object_id
        });
      }
    }
    return result;
  }, [])
);

export const getColorOptions = createSelector(
  getProduct,
  product => (product.options ? _head(product.options.filter(option => option.key === 'color')) : null)
);

export const getShapeOptions = createSelector(
  getProduct,
  product => (product.options ? _head(product.options.filter(option => option.key === 'silhouette')) : null)
);

export const getPaperTypeOptions = createSelector(
  getProduct,
  product => (product.options ? _head(product.options.filter(option => option.key === 'paper-type')) : null)
);

export const getSelectedValues = createSelector(
  getUuid,
  getCustomizations,
  getSkus,
  (uuid, customizations, skus) => {
    if (customizations[uuid]) {
      const sku = customizations[uuid].sku_object_id;
      if (skus[sku]) {
        return skus[sku];
      }
    }
    return null;
  }
);

export const getSkuId = createSelector(
  getUuid,
  getCustomizations,
  (uuid, customizations) => {
    if (customizations[uuid]) {
      return customizations[uuid].sku_object_id;
    }
    return null;
  }
);

export const getProjectObjectIds = createSelector(
  getCustomizationTypeUUIDs,
  getCustomizations,
  getSkus,
  (uuids, customizations, skus) => {
    const skuIds = [];
    Object.keys(uuids).forEach((uuid) => {
      if (INVITES_PRODUCT_TYPES.includes(uuid)) {
        const customization = customizations[uuids[uuid]];
        if (customization && customization.sku_object_id) {
          const sku = skus[customization.sku_object_id];
          if (sku) {
            skuIds.push(sku.product_object_id);
          }
        }
      }
    });
    return skuIds;
  }
);


export const getSelectedQuantity = createSelector(
  getUuid,
  getCustomizations,
  (uuid, customizations) => {
    if (customizations[uuid]) {
      return customizations[uuid].quantity;
    }
    return 100;
  }
);

export const getCurrentRoute = createSelector(
  getPathName,
  pathname => pathname.split('/').pop()
);

export const getIsAddressingOptionSet = createSelector(
  getCurrentRoute,
  getSteps,
  getCustomizations,
  getCustomizationTypeUUIDs,
  (route, steps, customizations, uuids) => {
    if (steps[route]) {
      const { cardType } = steps[route];
      const customization = customizations[uuids[cardType]];
      if (customization) {
        return customization.recipient_addressing;
      }
    }
    return false;
  }
);

export const getIsRSVPOptionSet = createSelector(
  getCurrentRoute,
  getSteps,
  getCustomizations,
  getCustomizationTypeUUIDs,
  (route, steps, customizations, uuids) => {
    if (steps[route] && steps[route].cardType) {
      const { cardType } = steps[route];
      const customization = customizations[uuids[cardType]];
      if (customization) {
        const { deleted_at } = customization;
        // disable eslint because of camelcase warning https://eslint.org/docs/2.0.0/rules/camelcase
        return !deleted_at; // eslint-disable-line
      }
    }
    return false;
  }
);

export const getRSVPPrice = createSelector(
  getCustomizations,
  getSkus,
  getCustomizationTypeUUIDs,
  (customizations, skus, uuids) => {
    const customization = customizations[uuids.RSVP];
    if (customization) {
      const sku = skus[customization.sku_object_id];
      if (sku) {
        return sku.price_cents;
      }
      return 0;
    }
    return 0;
  }
);

/**
 * Loop throu list of sections and construct section details
 * TODO: Extract all general code to a helper since it seems repetitive with a few exceptions
 */
export const getProjectSubtotalSummary = createSelector(
  getCustomizations,
  getSkus,
  getCustomizationTypeUUIDs,
  (costumizations, skus, uuids) => {
    let sections = COSTUMIZATION_BAR_DROPDOWN_SECTIONS;
    let customization;
    let sku;
    sections.forEach((section) => {
      switch (section.label) {
        case 'BACK':
          customization = costumizations[uuids.INVITATION];
          if (!customization) {
            sections = _reject(sections, o => o.label === section.label);
            return false;
          }
          sku = skus[customization.sku_object_id];
          if (sku) {
            const hasReversePrinting = _get(sku, 'product_option_values.reverse-printing');
            if (!hasReversePrinting || hasReversePrinting === 'none') {
              sections = _reject(sections, o => o.label === section.label);
            } else {
              _assign(section, { price: (sku.price_cents / 100) });
            }
          }
          break;
        case 'ENVELOPE':
          customization = costumizations[uuids.INVITATION_ENVELOPE];
          if (!customization) {
            sections = _reject(sections, o => o.label === section.label);
            return false;
          }
          sku = skus[customization.sku_object_id];
          if (sku) {
            const hasLiner = _get(sku, 'product_option_values.envelope-lining');
            if (hasLiner) {
              _assign(section, { hasLiner: hasLiner !== 'none' });
            }
            _assign(section, { price: (sku.price_cents / 100) });
          }
          break;
        case 'GUESTS': {
          customization = costumizations[uuids.INVITATION_ENVELOPE];
          if (!customization) {
            sections = _reject(sections, o => o.label === section.label);
            return false;
          }
          sku = skus[customization.sku_object_id];
          const hasGuestAddressing = _get(customization, 'recipient_addressing');
          if (!hasGuestAddressing) {
            sections = _reject(sections, o => o.label === section.label);
          }
          if (sku) {
            _assign(section, { price: (sku.price_cents / 100) });
          }
          break;
        }
        case 'RSVP CARD': {
          customization = costumizations[uuids.RSVP];
          if (!customization) {
            sections = _reject(sections, o => o.label === section.label);
            return false;
          }
          const rsvpCustomization = costumizations[uuids.RSVP];
          if (rsvpCustomization && rsvpCustomization.deleted_at) {
            sections = _reject(sections, o => o.label === section.label);
          } else {
            sku = skus[customization.sku_object_id];
            if (sku) {
              _assign(section, { price: (sku.price_cents / 100) });
            }
          }
          break;
        }
        case 'RSVP ENVELOPE': {
          customization = costumizations[uuids.RSVP_ENVELOPE];
          if (!customization) {
            sections = _reject(sections, o => o.label === section.label);
            return false;
          }
          const rsvpCustomization = costumizations[uuids.RSVP_ENVELOPE];
          if (rsvpCustomization && rsvpCustomization.deleted_at) {
            sections = _reject(sections, o => o.label === section.label);
          } else {
            sku = skus[customization.sku_object_id];
            if (sku) {
              _assign(section, { price: (sku.price_cents / 100) });
            }
          }
          break;
        }
        default:
      }
      return false;
    });
    return sections.sort();
  }
);

export const getProjectOrderView = createSelector(
  getCustomizations,
  getSkus,
  (customizations, skus) => (
    Object.keys(customizations).reduce((result, uuid) => {
      const customization = customizations[uuid];
      if (customization) {
        const sku = skus[customization.sku_object_id];
        // make sure not to include any deleted customizations in order view
        // getProjectOrderView is used in subtotal selector so it's important to filter out deleted
        // customizations to avoid messing up subtotal
        if (!customization.deleted_at && sku) {
          const orderView = {
            customizationUUID: customization.uuid,
            name: customization.name,
            type: customization.type,
            quantity: customization.quantity,
            priceCents: sku.price_cents,
            imageUrls: sku.image_urls,
            color: sku.product_option_values.color,
            colorLable: sku.product_option_value_labels.color,
            size: sku.product_option_values.size,
            liningColor: sku.product_option_values['envelope-lining-color']
          };
          result.push(orderView);
        }
      }
      return result;
    }, [])
  )
);

// calculate the subtotal using the the order view objects created by the
// getProjectOrderView selector
export const getOrderSubtotal = createSelector(
  getProjectOrderView,
  (orders) => {
    let subtotal = 0;
    if (orders.length > 0) {
      orders.forEach((order) => {
        if (order.priceCents > 0) {
          subtotal += ((order.quantity * order.priceCents) / 100);
        }
      });
    }
    return subtotal;
  }
);



/** WEBPACK FOOTER **
 ** ./src/selectors/cards/Customization.js
 **/